import React, { useState, useEffect, useRef } from 'react';
import { db } from '../utils/init-firebase';
import { collection, doc, onSnapshot, addDoc, updateDoc, setDoc, deleteDoc, query, orderBy, getDocs, writeBatch, where, limit } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import {
    Box,
    Heading,
    Text,
    Spinner,
    Center,
    useToast,
    VStack,
    HStack,
    Button,
    FormControl,
    FormLabel,
    Input,
    Switch,
    SimpleGrid,
    Flex,
    useColorModeValue,
    Icon,
    IconButton,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Avatar,
    Divider
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { FiCheckCircle, FiTrash2, FiEdit, FiPlus } from 'react-icons/fi';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useHistory } from 'react-router-dom';
import moment from 'moment';

// Default activities if none are set on a challenge
const defaultActivities = {
    'OUTBOUND_CALL_VM': { label: 'Outbound Call (Voicemail)', points: 2 },
    'HANDWRITTEN_CARD': { label: 'Handwritten Card Mailed', points: 2 },
    'OUTBOUND_CALL_CONTACT': { label: 'Outbound Call (Contact Made)', points: 5 },
    'SOCIAL_MEDIA_VIDEO': { label: 'Business Video on Social Media', points: 5 },
    'NETWORKING_EVENT': { label: 'Networking Event Attended', points: 5 },
    'RE_OFFICE_STOPBY': { label: 'Stopped by Real Estate Office', points: 10 },
    'SMALL_BIZ_STOPBY': { label: 'Stopped by Small Business', points: 10 },
};


// --- Activity Management Modal ---
const ManageActivitiesModal = ({ isOpen, onClose, challenge, onUpdateActivities }) => {
    const [activities, setActivities] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const toast = useToast();

    useEffect(() => {
        // Initialize with challenge's activities or defaults
        setActivities(challenge.activityConfig || defaultActivities);
    }, [challenge]);

    const handleActivityChange = (key, field, value) => {
        setActivities(prev => ({
            ...prev,
            [key]: { ...prev[key], [field]: value }
        }));
    };

    const handleAddActivity = () => {
        const newKey = `CUSTOM_${Date.now()}`;
        setActivities(prev => ({
            ...prev,
            [newKey]: { label: 'New Activity', points: 1 }
        }));
    };

    const handleRemoveActivity = (key) => {
        const newActivities = { ...activities };
        delete newActivities[key];
        setActivities(newActivities);
    };

    const handleSaveChanges = async () => {
        setIsSaving(true);
        try {
            await onUpdateActivities(challenge.id, { activityConfig: activities });
            toast({ title: "Activities updated successfully!", description: "Leaderboards will be recalculated shortly.", status: "success" });
            onClose();
        } catch (error) {
            console.error("Error updating activities:", error);
            toast({ title: "Error", description: "Could not update activities.", status: "error" });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Manage Activities for "{challenge.name}"</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        {Object.entries(activities).map(([key, value]) => (
                            <HStack key={key} spacing={3}>
                                <FormControl flex="3">
                                    <FormLabel srOnly>Activity Name</FormLabel>
                                    <Input
                                        value={value.label}
                                        onChange={(e) => handleActivityChange(key, 'label', e.target.value)}
                                        placeholder="Activity Name"
                                    />
                                </FormControl>
                                <FormControl flex="1">
                                    <FormLabel srOnly>Points</FormLabel>
                                    <NumberInput
                                        value={value.points}
                                        onChange={(val) => handleActivityChange(key, 'points', parseInt(val) || 0)}
                                        min={0}
                                    >
                                        <NumberInputField placeholder="Points" />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                </FormControl>
                                <IconButton
                                    icon={<FiTrash2 />}
                                    aria-label="Remove Activity"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={() => handleRemoveActivity(key)}
                                />
                            </HStack>
                        ))}
                        <Button leftIcon={<FiPlus />} onClick={handleAddActivity} variant="outline">
                            Add Activity
                        </Button>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button colorScheme="blue" onClick={handleSaveChanges} isLoading={isSaving}>
                        Save Changes
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};


// Card component for displaying a single challenge
const ChallengeCard = ({ challenge, isDefault, onSetDefault, onToggleHidden, onDelete, onUpdate, onUpdateActivities }) => {
    const cardBg = useColorModeValue('white', 'gray.800');
    const defaultBorderColor = useColorModeValue('blue.500', 'blue.300');
    const datepickerClass = useColorModeValue("chakra-input css-1c6j008", "chakra-input css-xpongc");

    // Hooks for controlling modals/dialogs
    const { isOpen: isDeleteAlertOpen, onOpen: onOpenDeleteAlert, onClose: onCloseDeleteAlert } = useDisclosure();
    const { isOpen: isEditModalOpen, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure();
    const { isOpen: isActivitiesModalOpen, onOpen: onOpenActivitiesModal, onClose: onCloseActivitiesModal } = useDisclosure();
    const cancelRef = useRef();

    // State for the edit form
    const [editedName, setEditedName] = useState(challenge.name);
    const [editedStartDate, setEditedStartDate] = useState(new Date(challenge.startDate));
    const [editedEndDate, setEditedEndDate] = useState(new Date(challenge.endDate));

    // Handler for the final delete confirmation
    const handleDeleteConfirm = () => {
        onDelete(challenge.id);
        onCloseDeleteAlert();
    };

    // Handler for saving edited changes
    const handleUpdateConfirm = () => {
        const updatedData = {
            name: editedName,
            startDate: editedStartDate.toISOString(),
            endDate: editedEndDate.toISOString(),
        };
        onUpdate(challenge.id, updatedData);
        onCloseEditModal();
    };

    return (
        <>
            <Box
                p={5}
                shadow="md"
                borderWidth="1px"
                borderRadius="lg"
                bg={cardBg}
                borderColor={isDefault ? defaultBorderColor : 'transparent'}
                border={isDefault ? '2px' : '1px'}
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
            >
                <VStack align="stretch" spacing={4}>
                    <HStack justifyContent="space-between" alignItems="flex-start">
                        <Box flex="1" pr={2}>
                            <Heading size="md">{challenge.name}</Heading>
                            <Text fontSize="sm" color="gray.500" mt={1}>
                                {new Date(challenge.startDate).toLocaleDateString()} - {new Date(challenge.endDate).toLocaleDateString()}
                            </Text>
                        </Box>
                        <HStack>
                           <IconButton
                                icon={<FiEdit />}
                                aria-label="Edit challenge"
                                variant="ghost"
                                onClick={onOpenEditModal}
                            />
                            <IconButton
                                icon={<FiTrash2 />}
                                aria-label="Delete challenge"
                                variant="ghost"
                                colorScheme="red"
                                onClick={onOpenDeleteAlert}
                            />
                        </HStack>
                    </HStack>
                    
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                        <FormLabel htmlFor={`hide-switch-${challenge.id}`} mb="0">
                            {challenge.isHidden ? 'Hidden' : 'Visible'}
                        </FormLabel>
                        <Switch
                            id={`hide-switch-${challenge.id}`}
                            isChecked={!challenge.isHidden}
                            onChange={() => onToggleHidden(challenge.id, !challenge.isHidden)}
                        />
                    </FormControl>
                    <Button
                        size="sm"
                        onClick={onOpenActivitiesModal}
                        variant="outline"
                    >
                        Manage Activities
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => onSetDefault(challenge.id)}
                        isDisabled={isDefault}
                        leftIcon={isDefault ? <Icon as={FiCheckCircle} /> : null}
                        colorScheme={isDefault ? 'blue' : 'gray'}
                    >
                        {isDefault ? 'Default Challenge' : 'Set as Default'}
                    </Button>
                </VStack>
            </Box>

            {/* Edit Challenge Modal */}
            <Modal isOpen={isEditModalOpen} onClose={onCloseEditModal}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Edit Challenge</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Challenge Name</FormLabel>
                                <Input value={editedName} onChange={(e) => setEditedName(e.target.value)} />
                            </FormControl>
                             <HStack spacing={4} w="full">
                                <FormControl isRequired>
                                    <FormLabel>Start Date</FormLabel>
                                    <DatePicker
                                        selected={editedStartDate}
                                        onChange={(date) => setEditedStartDate(date)}
                                        className={datepickerClass}
                                    />
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel>End Date</FormLabel>
                                    <DatePicker
                                        selected={editedEndDate}
                                        onChange={(date) => setEditedEndDate(date)}
                                        className={datepickerClass}
                                    />
                                </FormControl>
                            </HStack>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onCloseEditModal}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleUpdateConfirm}>
                            Save Changes
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            
            {/* Manage Activities Modal */}
            <ManageActivitiesModal
                isOpen={isActivitiesModalOpen}
                onClose={onCloseActivitiesModal}
                challenge={challenge}
                onUpdateActivities={onUpdateActivities}
            />

            {/* Delete Confirmation Dialog */}
            <AlertDialog
                isOpen={isDeleteAlertOpen}
                leastDestructiveRef={cancelRef}
                onClose={onCloseDeleteAlert}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Challenge
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure you want to delete the challenge "{challenge.name}"? This action cannot be undone.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onCloseDeleteAlert}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

// --- Global Activity Log Component ---
const GlobalActivityLog = ({ activityLog, challenges }) => {
    const cardBg = useColorModeValue('white', 'gray.800');
    const headingColor = useColorModeValue('gray.700', 'white');
    const activityItemBg = useColorModeValue('gray.50', 'gray.700');

    const getChallengeName = (challengeId) => {
        const challenge = challenges.find(c => c.id === challengeId);
        return challenge ? challenge.name : 'Unknown Challenge';
    };

    return (
        <Card w={{base: '100%', lg: '30%'}} h="fit-content" maxH="85vh">
            <Heading size="md" color={headingColor} mb={4}>Live Activity Feed</Heading>
            <VStack spacing={3} align="stretch" overflowY="auto" maxH="75vh">
                {activityLog.length > 0 ? activityLog.map(activity => (
                    <Box key={activity.id} p={3} bg={activityItemBg} borderRadius="md">
                        <HStack>
                            <Avatar size="sm" name={activity.userName} src={activity.userPhoto} />
                            <VStack align="start" spacing={0}>
                                <Text fontSize="sm">
                                    <Text as="span" fontWeight="bold">{activity.userName}</Text> logged "{activity.activityType ? (challenges.find(c => c.id === activity.challengeId)?.activityConfig?.[activity.activityType]?.label || defaultActivities[activity.activityType]?.label) : 'an activity'}"
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                    in "{getChallengeName(activity.challengeId)}" • {moment(activity.timestamp?.toDate()).fromNow()}
                                </Text>
                            </VStack>
                        </HStack>
                    </Box>
                )) : <Text color="gray.500">No activities logged yet.</Text>}
            </VStack>
        </Card>
    );
};

// Reusable Card component for consistent styling
const Card = ({ children, ...props }) => {
    const cardBg = useColorModeValue('white', 'gray.800');
    return (
        <Box bg={cardBg} rounded="xl" shadow="lg" p={6} {...props}>
          {children}
        </Box>
    );
}

// Main settings page component
export default function SalesChallengeSettingsPage() {
    const { SuperAdmin } = useAuth();
    const toast = useToast();
    const history = useHistory();
    const datepickerClass = useColorModeValue("chakra-input css-1c6j008", "chakra-input css-xpongc");

    const [challenges, setChallenges] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [defaultChallengeId, setDefaultChallengeId] = useState('');
    const [newName, setNewName] = useState('');
    const [newStartDate, setNewStartDate] = useState(new Date());
    const [newEndDate, setNewEndDate] = useState(new Date());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activityLog, setActivityLog] = useState([]);

    useEffect(() => {
        if (!SuperAdmin) {
            setIsLoading(false);
            return;
        };

        // Subscribe to challenges collection
        const challengesQuery = query(collection(db, 'salesChallenges'), orderBy('startDate', 'desc'));
        const unsubscribeChallenges = onSnapshot(challengesQuery, (snapshot) => {
            const challengeList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setChallenges(challengeList);
            setIsLoading(false);
        });

        // Subscribe to settings document for default challenge
        const settingsDocRef = doc(db, 'settings', 'salesChallenge');
        const unsubscribeSettings = onSnapshot(settingsDocRef, (doc) => {
            if (doc.exists()) {
                setDefaultChallengeId(doc.data().defaultChallengeId || '');
            }
        });

        // Subscribe to global activity log
        const activityLogQuery = query(collection(db, 'salesChallengeActivities'), orderBy('timestamp', 'desc'), limit(50));
        const unsubscribeActivityLog = onSnapshot(activityLogQuery, (snapshot) => {
            const log = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setActivityLog(log);
        });

        // Cleanup subscriptions on unmount
        return () => {
            unsubscribeChallenges();
            unsubscribeSettings();
            unsubscribeActivityLog();
        };
    }, [SuperAdmin]);

    // Handler for creating a new challenge
    const handleCreateChallenge = async (e) => {
        e.preventDefault();
        if (!newName || !newStartDate || !newEndDate) {
            toast({ title: "All fields are required.", status: "error", duration: 3000 });
            return;
        }
        setIsSubmitting(true);
        try {
            await addDoc(collection(db, 'salesChallenges'), {
                name: newName,
                startDate: newStartDate.toISOString(),
                endDate: newEndDate.toISOString(),
                isHidden: false,
                activityConfig: defaultActivities, // Add default activities on creation
            });
            toast({ title: "Challenge created successfully!", status: "success" });
            setNewName('');
            setNewStartDate(new Date());
            setNewEndDate(new Date());
        } catch (error) {
            console.error("Error creating challenge:", error);
            toast({ title: "Error creating challenge.", status: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handler for setting a challenge as the default
    const handleSetDefault = async (challengeId) => {
        try {
            const settingsDocRef = doc(db, 'settings', 'salesChallenge');
            await setDoc(settingsDocRef, { defaultChallengeId: challengeId }, { merge: true });
            toast({ title: "Default challenge updated.", status: "success" });
        } catch (error) {
            console.error("Error setting default challenge:", error);
            toast({ title: "Error updating settings.", status: "error" });
        }
    };

    // Handler for toggling challenge visibility
    const handleToggleHidden = async (challengeId, shouldHide) => {
        try {
            const challengeDocRef = doc(db, 'salesChallenges', challengeId);
            await updateDoc(challengeDocRef, { isHidden: shouldHide });
            toast({ title: "Challenge visibility updated.", status: "info" });
        } catch (error) {
            console.error("Error updating challenge visibility:", error);
            toast({ title: "Error updating challenge.", status: "error" });
        }
    };

    // Handler for updating a challenge's details (name, dates, or activities)
    const handleUpdateChallenge = async (challengeId, updatedData) => {
        try {
            const challengeDocRef = doc(db, 'salesChallenges', challengeId);
            await updateDoc(challengeDocRef, updatedData);
            toast({ title: "Challenge updated successfully!", status: "success" });
        } catch (error) {
            console.error("Error updating challenge:", error);
            toast({ title: "Error updating challenge.", status: "error" });
        }
    };

    // Handler for deleting a challenge
    const handleDeleteChallenge = async (challengeId) => {
        try {
            if (challengeId === defaultChallengeId) {
                const settingsDocRef = doc(db, 'settings', 'salesChallenge');
                await setDoc(settingsDocRef, { defaultChallengeId: '' }, { merge: true });
                toast({ title: "Default challenge setting cleared.", status: "info", duration: 2000 });
            }
            
            const challengeDocRef = doc(db, 'salesChallenges', challengeId);
            await deleteDoc(challengeDocRef);
            
            toast({ title: "Challenge deleted successfully.", status: "success" });
        } catch (error) {
            console.error("Error deleting challenge:", error);
            toast({ title: "Error deleting challenge.", status: "error" });
        }
    };

    if (isLoading) {
        return <Center p={10}><Spinner size="xl" /></Center>;
    }
    
    if (!SuperAdmin) {
        return <Center p={10}><Text>You do not have permission to view this page.</Text></Center>;
    }

    return (
        <Box p={{ base: 4, md: 8 }}>
            <Flex align="center" mb={6}>
                <IconButton
                    icon={<ArrowBackIcon />}
                    onClick={() => history.push('/sales-challenge')}
                    aria-label="Go back to challenges"
                    variant="ghost"
                    mr={4}
                />
                <Heading>Sales Challenge Settings</Heading>
            </Flex>

            <Flex direction={{base: 'column', lg: 'row'}} gap={8}>
                <Box flex="1">
                    <Card mb={8}>
                        <Heading size="lg" mb={4}>Create New Challenge</Heading>
                        <form onSubmit={handleCreateChallenge}>
                            <VStack spacing={4} align="stretch">
                                <FormControl isRequired>
                                    <FormLabel>Challenge Name</FormLabel>
                                    <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g., Q3 Sales Sprint" />
                                </FormControl>
                                <HStack spacing={4}>
                                    <FormControl isRequired>
                                        <FormLabel>Start Date</FormLabel>
                                        <DatePicker
                                            selected={newStartDate}
                                            onChange={(date) => setNewStartDate(date)}
                                            className={datepickerClass}
                                        />
                                    </FormControl>
                                    <FormControl isRequired>
                                        <FormLabel>End Date</FormLabel>
                                        <DatePicker
                                            selected={newEndDate}
                                            onChange={(date) => setNewEndDate(date)}
                                            className={datepickerClass}
                                        />
                                    </FormControl>
                                </HStack>
                                <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
                                    Create Challenge
                                </Button>
                            </VStack>
                        </form>
                    </Card>

                    <Heading size="lg" mb={4}>Manage Existing Challenges</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        {challenges.map(challenge => (
                            <ChallengeCard
                                key={challenge.id}
                                challenge={challenge}
                                isDefault={challenge.id === defaultChallengeId}
                                onSetDefault={handleSetDefault}
                                onToggleHidden={handleToggleHidden}
                                onDelete={handleDeleteChallenge}
                                onUpdate={handleUpdateChallenge}
                                onUpdateActivities={handleUpdateChallenge}
                            />
                        ))}
                    </SimpleGrid>
                </Box>
                
                <GlobalActivityLog activityLog={activityLog} challenges={challenges} />
            </Flex>
        </Box>
    );
}
